import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Download, Plus, Trash2, ArrowLeft, Sparkles, RotateCcw, Building2, Save, Clock , FileText, FileUp, FileDown} from "lucide-react";
import { useAuth, useSettings } from "../App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BurndownChartForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const savedId = searchParams.get('saved');
  const { settings, activeSettings } = useSettings();
  const [selectedSetting, setSelectedSetting] = useState(activeSettings?.id || "none");
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = savedId;

  const getEmptyForm = () => ({
    sprint_name: "",
    sprint_number: 1,
    start_date: new Date().toISOString().split("T")[0],
    end_date: "",
    total_story_points: 0,
    daily_data: [{ day: 1, date: "", remaining_points: 0, ideal_remaining: 0, completed_points: 0, notes: "" }]
  });

  const getSampleData = () => {
    const startDate = new Date();
    const dailyData = [];
    const totalPoints = 40;
    const sprintDays = 10;
    
    for (let i = 0; i <= sprintDays; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      const idealRemaining = totalPoints - (totalPoints / sprintDays * i);
      const actualRemaining = Math.max(0, totalPoints - Math.floor(Math.random() * 5 + 3) * i);
      dailyData.push({
        day: i,
        date: date.toISOString().split("T")[0],
        remaining_points: i === 0 ? totalPoints : actualRemaining,
        ideal_remaining: Math.round(idealRemaining),
        completed_points: i === 0 ? 0 : totalPoints - actualRemaining,
        notes: i === 0 ? "Sprint Start" : i === sprintDays ? "Sprint End" : ""
      });
    }

    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + sprintDays);

    return {
      sprint_name: "Sprint 5 - User Authentication",
      sprint_number: 5,
      start_date: startDate.toISOString().split("T")[0],
      end_date: endDate.toISOString().split("T")[0],
      total_story_points: totalPoints,
      daily_data: dailyData
    };
  };

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Burndown Chart / Velocity Tracker Draft";
      const payload = {
        name: draftName,
        template_type: "Burndown Chart / Velocity Tracker",
        template_id: "burndown-chart",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  useEffect(() => {
    if (activeSettings?.id) setSelectedSetting(activeSettings.id);
  }, [activeSettings]);

  useEffect(() => {
    if (savedId) {
      const loadSavedTemplate = async () => {
        try {
          const response = await axios.get(`${API}/saved-templates/${savedId}`);
          if (response.data && response.data.data) {
            setFormData(response.data.data);
          }
        } catch (error) {
          console.error("Error loading saved template:", error);
        }
      };
      loadSavedTemplate();
    }
  }, [savedId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: formData.sprint_name || "Untitled Burndown Chart",
        template_type: "Burndown Chart",
        template_id: "burndown-chart",
        format: "XLSX",
        data: formData
      };
      
      if (savedId) {
        await axios.put(`${API}/saved-templates/${savedId}`, payload);
        alert("Template updated successfully!");
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload);
        alert("Template saved successfully!");
        navigate(`/template/burndown-chart?saved=${response.data.id}`);
      }
    } catch (error) {
      console.error("Error saving:", error);
      alert("Error saving template.");
    }
    setSaving(false);
  };

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "burndown-chart", "Burndown Chart", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("burndown-chart", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      let url = `${API}/generate/burndown-chart`;
      if (selectedSetting && selectedSetting !== "none") {
        url += `?settings_id=${selectedSetting}`;
      }
      const response = await axios.post(url, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Burndown_Chart_${formData.sprint_name || "Sprint"}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating document.");
    }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Burndown Chart / Velocity Tracker",
        formData,
        settings: activeSettings || {},
        templateType: "Burndown Chart / Velocity Tracker",
        templateId: "generate",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const updateDailyData = (index, field, value) => {
    const updated = [...formData.daily_data];
    updated[index][field] = field.includes("points") || field === "day" ? parseInt(value) || 0 : value;
    setFormData({ ...formData, daily_data: updated });
  };

  const addDay = () => {
    const lastDay = formData.daily_data[formData.daily_data.length - 1];
    const newDay = lastDay ? lastDay.day + 1 : 1;
    setFormData({
      ...formData,
      daily_data: [...formData.daily_data, { day: newDay, date: "", remaining_points: 0, ideal_remaining: 0, completed_points: 0, notes: "" }]
    });
  };

  const removeDay = (index) => {
    setFormData({
      ...formData,
      daily_data: formData.daily_data.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Burndown Chart / Velocity Tracker</h1>
                <p className="text-sm text-gray-500">Track sprint progress and team velocity</p>
              </div>
            </div>
            <div className="flex gap-2 items-center">
              {settings.length > 0 && (
                <Select value={selectedSetting} onValueChange={setSelectedSetting}>
                  <SelectTrigger className="w-[180px]">
                    <Building2 className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Header/Footer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Header/Footer</SelectItem>
                    {settings.map(s => <SelectItem key={s.id} value={s.id}>{s.name} {s.is_default && "★"}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn">
                <Sparkles className="h-4 w-4 mr-2" /> Sample
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn">
                <RotateCcw className="h-4 w-4 mr-2" /> Reset
              </Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Sprint Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <Label>Sprint Name *</Label>
                <Input value={formData.sprint_name} onChange={(e) => setFormData({ ...formData, sprint_name: e.target.value })} placeholder="Sprint 5" data-testid="sprint-name-input" />
              </div>
              <div>
                <Label>Sprint Number</Label>
                <Input type="number" value={formData.sprint_number} onChange={(e) => setFormData({ ...formData, sprint_number: parseInt(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>Start Date</Label>
                <Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} />
              </div>
              <div>
                <Label>End Date</Label>
                <Input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} />
              </div>
              <div>
                <Label>Total Story Points</Label>
                <Input type="number" value={formData.total_story_points} onChange={(e) => setFormData({ ...formData, total_story_points: parseInt(e.target.value) || 0 })} />
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <Label className="text-lg font-semibold">Daily Progress Data</Label>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-2 text-left">Day</th>
                      <th className="p-2 text-left">Date</th>
                      <th className="p-2 text-left">Remaining Points</th>
                      <th className="p-2 text-left">Ideal Remaining</th>
                      <th className="p-2 text-left">Completed Points</th>
                      <th className="p-2 text-left">Notes</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.daily_data.map((day, index) => (
                      <tr key={index} className="border-b">
                        <td className="p-2">
                          <Input type="number" value={day.day} onChange={(e) => updateDailyData(index, "day", e.target.value)} className="w-16" />
                        </td>
                        <td className="p-2">
                          <Input type="date" value={day.date} onChange={(e) => updateDailyData(index, "date", e.target.value)} className="w-36" />
                        </td>
                        <td className="p-2">
                          <Input type="number" value={day.remaining_points} onChange={(e) => updateDailyData(index, "remaining_points", e.target.value)} className="w-24" />
                        </td>
                        <td className="p-2">
                          <Input type="number" value={day.ideal_remaining} onChange={(e) => updateDailyData(index, "ideal_remaining", e.target.value)} className="w-24" />
                        </td>
                        <td className="p-2">
                          <Input type="number" value={day.completed_points} onChange={(e) => updateDailyData(index, "completed_points", e.target.value)} className="w-24" />
                        </td>
                        <td className="p-2">
                          <Input value={day.notes} onChange={(e) => updateDailyData(index, "notes", e.target.value)} placeholder="Notes" />
                        </td>
                        <td className="p-2">
                          <Button variant="ghost" size="sm" onClick={() => removeDay(index)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button variant="outline" onClick={addDay}>
                <Plus className="h-4 w-4 mr-2" /> Add Day
              </Button>
            </div>

            <div className="flex gap-4">
              <Button variant="outline" onClick={handleSave} disabled={saving || !formData.sprint_name} className="flex-1" size="lg" data-testid="save-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || !formData.sprint_name} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download XLSX"}
              </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                <FileText className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default BurndownChartForm;
